// ── Shared devices cache (C1389) ──
//
// First web-side caller of GET /api/devices — the web app never queried this endpoint
// before (see ai/architecture/tt-api-devices.md). Gates the task-detail panel's
// "Start in Task App" button and its device section: a device counts as "linked" when its
// project_ids includes the current project, meaning some machine has run Task App setup and bound
// it there, or Task App linked its own device when the project opted in to remote control.
//
// Same memoize-per-session discipline as lib/project.js's loadProject() — one fetch per page
// lifetime, fails open to [] so a devices hiccup only disables the button, never blocks the
// panel. Links change only on other machines, never from this page, so nothing here invalidates
// on its own: reloadDevices() re-reads them when the relay announces a device coming online
// (TPT9217).

import { api } from '../api.js';

let _cache = null; // Promise<Device[]> | null

// Fetches `GET /devices` — user-scoped, no project nesting (see tt-api-devices.md).
export function loadDevices() {
  if (!_cache) {
    _cache = api.get('/devices')
      .then(res => res.devices || [])
      .catch(() => []);
  }
  return _cache;
}

// A fresh `GET /devices` that replaces the cached list. A failed read keeps the list it had, so a
// hiccup never hides a device that was already known.
export function reloadDevices() {
  const previous = _cache;
  _cache = api.get('/devices')
    .then(res => res.devices || [])
    .catch(() => previous || []);
  return _cache;
}

// The signed-in user's devices with a live relay connection for `projectId` (TPT495): the one list
// of start targets, shared with the relay. Never cached, since presence is what it answers, and
// unlike loadDevices() it rejects, so a caller can tell "none online" from "could not check".
export function loadOnlineDevices(projectId) {
  return api.get(`/devices/online?project_id=${encodeURIComponent(projectId)}`).then(res => res.devices || []);
}

// True when some device of the signed-in user has `projectId` in its project_ids.
export async function hasLinkedDevice(projectId) {
  const devices = await loadDevices();
  const target = String(projectId);
  return devices.some(d => (d.project_ids || []).some(id => String(id) === target));
}
